import { IconCalendarUi, IconLink, IconPhone, IconVideo } from '../crm/crmUi'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useAppMode } from '../../contexts/AppModeContext'
import { useAuth } from '../../contexts/AuthContext'
import { addTask, getDealById, getMySchedule, updateBooking, type Booking, type ScheduleFeed } from '../crm/crmApi'
import { DealDetail } from '../crm/DealDetail'
import { LeadRedirect } from '../crm/CrmLeadPage'
import { BookingModal } from '../crm/BookingModal'
import { addFollowUpTodoOnce } from '../todo/useTodos'

/**
 * CALLS dock (Lee, 31 Jul 2026 — Phase 2) — right-edge tab, SUPPORT MODE ONLY.
 *
 * The tab is DRAGGABLE vertically (same pattern as the TO-DO notebook; position
 * persists). The panel is a VERTICAL DAY CALENDAR — hour grid with positioned
 * blocks, like the scheduler's side panel: CRM calls (green) and meetings (blue)
 * solid, Calendly demos (purple) and Google events (grey) dashed, red now-line on
 * today, all-day events pinned above the grid. ‹ › day arrows, mini-calendar
 * dropdown, Today pill. Clicking a block opens its action bar (call / join /
 * complete-with-forced-log); Phase 3 expands that into the full detail card.
 */

const pad = (n: number) => String(n).padStart(2, '0')
const toDayIso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const fmtTime = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '')
const minsOf = (iso: string) => { const d = new Date(iso); return d.getHours() * 60 + d.getMinutes() }

export interface DockItem {
  key: string
  kind: 'call' | 'meeting' | 'google' | 'demo'
  /** A colleague's demo — visible for awareness, not for attending. */
  informational?: boolean
  title: string
  startAt: string | null
  endAt: string | null
  sub?: string
  meetUrl?: string | null
  phone?: string | null
  allDay?: boolean
  bookingId?: string
  status?: string
  overdue?: boolean
  dealId?: string | null
  dealName?: string | null
  matchedBy?: string | null
  attendees?: string[]
  note?: string | null
  logText?: string | null
  logLinks?: string[]
  contactName?: string | null
}

/** Colour rules (Lee, 3 Aug 2026 rev 2):
 *  • Everything that is YOURS — calls, meetings, your own demos — is BLUE, in the
 *    soft-tint + dashed style. Type stays obvious from the icon.
 *  • Anything on your list that is NOT yours (a colleague's demo) is GREY /
 *    light-black — visible for awareness, "info only", not yours to join.
 *  • Google Calendar context events are a lighter grey again. */
const MINE_STYLE = { bg: '#EFF6FF', border: '#3B82F6', text: '#1E3A8A', dashed: true }
const KIND_STYLE: Record<DockItem['kind'], { bg: string; border: string; text: string; dashed?: boolean; icon: React.ReactNode }> = {
  call: { ...MINE_STYLE, icon: <IconPhone className="w-3 h-3 inline -mt-0.5" /> },
  meeting: { ...MINE_STYLE, icon: <IconCalendarUi className="w-3 h-3 inline -mt-0.5" /> },
  demo: { ...MINE_STYLE, icon: <IconVideo className="w-3 h-3 inline -mt-0.5" /> },
  google: { bg: '#F8FAFC', border: '#CBD5E1', text: '#64748B', dashed: true, icon: <IconCalendarUi className="w-3 h-3 inline -mt-0.5" /> },
}
const INFO_STYLE = { bg: '#F3F4F6', border: '#4B5563', text: '#374151', dashed: true, icon: <IconVideo className="w-3 h-3 inline -mt-0.5" /> }

/** Compact month grid for the date dropdown. */
const MiniCalendar: React.FC<{ day: string; onPick: (iso: string) => void }> = ({ day, onPick }) => {
  const sel = new Date(`${day}T12:00:00`)
  const [view, setView] = useState(() => new Date(sel.getFullYear(), sel.getMonth(), 1))
  const todayIso = toDayIso(new Date())
  const firstDow = (view.getDay() + 6) % 7
  const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate()
  const cells: (string | null)[] = [
    ...Array.from({ length: firstDow }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => toDayIso(new Date(view.getFullYear(), view.getMonth(), i + 1))),
  ]
  return (
    <div className="rounded-xl bg-surface border border-border shadow-float p-3 w-64" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-2">
        <button onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))} className="w-6 h-6 rounded text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover">‹</button>
        <p className="text-[12px] font-bold text-txt-primary">{view.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</p>
        <button onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))} className="w-6 h-6 rounded text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover">›</button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <span key={i} className="text-[9px] font-bold text-txt-tertiary py-0.5">{d}</span>
        ))}
        {cells.map((iso, i) =>
          iso ? (
            <button
              key={i}
              onClick={() => onPick(iso)}
              className={`h-7 rounded-md text-[11px] font-medium transition-colors ${
                iso === day ? 'bg-brand-600 text-white' : iso === todayIso ? 'text-brand-600 font-bold hover:bg-surface-hover' : 'text-txt-secondary hover:bg-surface-hover'
              }`}
            >
              {Number(iso.slice(8))}
            </button>
          ) : (
            <span key={i} />
          ),
        )}
      </div>
    </div>
  )
}

// 72px per hour (Lee: 48 was too cluttered) — blocks breathe, the grid scrolls.
const HOUR_H = 72

export const CallsDock: React.FC = () => {
  const { mode } = useAppMode()
  const { currentAgent } = useAuth()
  const [editing, setEditing] = useState<{ booking: Booking; dealId: string; dealName?: string } | null>(null)
  const [followingUp, setFollowingUp] = useState<{ booking: Booking; dealId: string; dealName?: string } | null>(null)
  const [open, setOpen] = useState(false)
  const [day, setDay] = useState(() => toDayIso(new Date()))
  const [feed, setFeed] = useState<ScheduleFeed | null>(null)
  const [calOpen, setCalOpen] = useState(false)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [logText, setLogText] = useState('')
  const [logging, setLogging] = useState(false)
  const [openDeal, setOpenDeal] = useState<any | null>(null)
  const [dealLoading, setDealLoading] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Draggable tab (TO-DO notebook pattern): vertical position persists.
  const [tabY, setTabY] = useState<number>(() => {
    try { const v = parseInt(localStorage.getItem('wq_calls_tab_y') || '', 10); return isNaN(v) ? Math.round(window.innerHeight * 0.58) : v } catch { return 420 }
  })
  const tabDown = (e: React.PointerEvent) => {
    e.preventDefault()
    const startY = e.clientY
    const startTab = tabY
    let moved = false
    let lastY = startTab
    const onMove = (ev: PointerEvent) => {
      const dy = ev.clientY - startY
      if (Math.abs(dy) > 4) moved = true
      if (!moved) return
      lastY = Math.max(70, Math.min(window.innerHeight - 140, startTab + dy))
      setTabY(lastY)
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      if (moved) {
        try { localStorage.setItem('wq_calls_tab_y', String(lastY)) } catch { /* ignore */ }
      } else {
        setOpen((v) => !v)
      }
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const load = () => {
    getMySchedule(day, day).then((f) => {
      setFeed(f)
      // AUTO follow-up (personal half): any cancelled booking assigned to ME gets a
      // to-do notebook entry, once per booking per device. The lead-side task is
      // created by the server the moment the cancellation lands.
      if (currentAgent?.id) {
        for (const b of f.bookings) {
          if (b.status === 'cancelled' && String(b.assigneeId) === String(currentAgent.id)) {
            const when = b.startAt ? ` (was ${new Date(b.startAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })})` : ''
            addFollowUpTodoOnce(String(currentAgent.id), b.id, `Follow up: cancelled ${b.type} "${b.title}"${when}${b.dealName ? ` — ${b.dealName}` : ''}`)
          }
        }
      }
    }).catch(() => setFeed({ bookings: [], google: { available: false, events: [] }, demos: [] }))
  }
  useEffect(() => {
    if (!open) return
    setFeed(null)
    setSelectedKey(null)
    setLogging(false)
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, day])

  // Merge + de-dupe the three sources (memo BEFORE any conditional return — hook rules).
  // De-dupe is by IDENTITY only: a Google event is dropped when it IS a CRM booking's
  // mirrored event (googleEventId) or shares its Meet link. NEVER by start time — a
  // same-minute rule once swallowed a real customer meeting that happened to start
  // when a test booking did.
  const items: DockItem[] = useMemo(() => {
    if (!feed) return []
    const out: DockItem[] = []
    const taken = new Set<string>()
    feed.bookings.forEach((b) => {
      if (b.googleEventId) taken.add(b.googleEventId)
      if (b.meetUrl) taken.add(b.meetUrl)
      out.push({
        key: b.id, kind: b.type, title: b.title, startAt: b.startAt, endAt: b.endAt,
        sub: [b.contactName, b.dealName].filter(Boolean).join(' · '),
        meetUrl: b.meetUrl, phone: b.phone, bookingId: b.id, status: b.status, overdue: b.overdue,
        dealId: b.dealId, dealName: b.dealName, attendees: b.attendees, note: b.note,
        logText: b.logText, logLinks: b.logLinks, contactName: b.contactName,
      })
    })
    feed.demos.forEach((d) => {
      if (d.meetUrl) taken.add(d.meetUrl)
      // ALL demos show (any host) — labelled with whose demo it is. A colleague's
      // demo is INFORMATIONAL for this viewer (amber) — the host sees it normally.
      const hostLabel = d.mine ? 'your demo' : d.hostEmail ? `hosted by ${d.hostEmail} — info only` : 'host unassigned'
      out.push({
        key: d.id, kind: 'demo', title: `Demo — ${d.title}`, startAt: d.startAt, endAt: d.endAt,
        sub: [[d.inviteeName, d.dealName].filter(Boolean).join(' · '), hostLabel].filter(Boolean).join(' · '),
        meetUrl: d.meetUrl, dealId: d.dealId, dealName: d.dealName,
        informational: !d.mine,
      })
    })
    feed.google.events
      .filter((e) => e.allDay || toDayIso(new Date(e.startAt)) === day)
      .filter((e) => !(e.gid && taken.has(e.gid)) && !(e.meetUrl && taken.has(e.meetUrl)))
      .forEach((e, i) => {
        out.push({
          key: `g${i}`, kind: 'google', title: e.title, startAt: e.startAt, endAt: e.endAt,
          meetUrl: e.meetUrl, allDay: e.allDay,
          sub: e.dealName ? `${e.dealName}${e.matchedBy === 'domain' ? ' · matched by company domain' : ''}` : undefined,
          dealId: e.dealId, dealName: e.dealName, matchedBy: e.matchedBy, attendees: e.attendees,
        })
      })
    // Held/finished items age off the LIVE view 1 hour after they end (Lee,
    // 19 Aug 2026) — an over-and-done meeting is clutter, not schedule. Only
    // applies when viewing TODAY: browsing a past date deliberately still shows
    // the full day. End time falls back to start+45m when none is stored.
    const today = toDayIso(new Date())
    if (day === today) {
      const cutoff = Date.now() - 3600_000
      return out.filter((i) => {
        if (i.allDay || !i.startAt) return true
        const end = i.endAt ? new Date(i.endAt).getTime() : new Date(i.startAt).getTime() + 45 * 60_000
        return end >= cutoff
      })
    }
    return out
  }, [feed, day])

  // Raw booking objects by id — the Edit modal needs the full record, not the DockItem.
  const bookingById = useMemo(() => {
    const out: Record<string, Booking> = {}
    for (const b of feed?.bookings || []) out[b.id] = b
    return out
  }, [feed])

  const timed = items.filter((i) => !i.allDay && i.startAt)
  const allDay = items.filter((i) => i.allDay)

  // Overlap layout, Google-Calendar style (Lee: offset stacking was confusing):
  // overlapping events form a CLUSTER; the cluster's time rows split into equal
  // side-by-side columns, one per event, so nothing ever covers anything.
  const lanes: Record<string, number> = {}
  const cols: Record<string, number> = {}
  {
    const sorted = [...timed].sort((a, b) => minsOf(a.startAt!) - minsOf(b.startAt!))
    const active: { end: number; lane: number }[] = []
    let cluster: string[] = []
    let clusterEnd = -1
    const flush = () => {
      const n = cluster.length ? Math.max(...cluster.map((k) => lanes[k])) + 1 : 1
      cluster.forEach((k) => { cols[k] = n })
      cluster = []
    }
    for (const it of sorted) {
      const from = minsOf(it.startAt!)
      const to = it.endAt ? minsOf(it.endAt) : from + 30
      if (cluster.length && from >= clusterEnd) { flush(); active.length = 0; clusterEnd = -1 }
      for (let i = active.length - 1; i >= 0; i--) if (active[i].end <= from) active.splice(i, 1)
      const used = new Set(active.map((a) => a.lane))
      let lane = 0
      while (used.has(lane)) lane++
      lanes[it.key] = lane
      active.push({ end: to, lane })
      cluster.push(it.key)
      clusterEnd = Math.max(clusterEnd, to)
    }
    flush()
  }

  // Grid range 07–21, stretched to fit outliers.
  const starts = timed.map((i) => minsOf(i.startAt!))
  const ends = timed.map((i) => (i.endAt ? minsOf(i.endAt) : minsOf(i.startAt!) + 30))
  const startH = Math.min(7, ...(starts.length ? [Math.floor(Math.min(...starts) / 60)] : [7]))
  const endH = Math.max(21, ...(ends.length ? [Math.ceil(Math.max(...ends) / 60)] : [21]))
  const gridMin = startH * 60
  const gridH = (endH - startH) * HOUR_H
  const topOf = (min: number) => ((min - gridMin) / 60) * HOUR_H
  const heightOf = (a: number, b: number) => Math.max(30, ((b - a) / 60) * HOUR_H - 3)

  const todayIso = toDayIso(new Date())
  const isToday = day === todayIso
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes()

  // Open scrolled to the first item (or 8am).
  useEffect(() => {
    if (!open || feed === null) return
    const first = starts.length ? Math.min(...starts) : 8 * 60
    scrollRef.current?.scrollTo({ top: Math.max(0, topOf(first) - 60) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, feed === null, day])

  // SUPPORT MODE ONLY — after all hooks.
  if (mode !== 'support') return null

  const shiftDay = (delta: number) => {
    const d = new Date(`${day}T12:00:00`)
    d.setDate(d.getDate() + delta)
    setDay(toDayIso(d))
  }
  const dayLabel = new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })
  const selected = items.find((i) => i.key === selectedKey) || null

  const completeSelected = () => {
    if (!selected?.bookingId) return
    if (selected.kind === 'call') {
      if (!logging) { setLogging(true); setLogText(''); return }
      const text = logText.trim()
      if (!text) return
      updateBooking(selected.bookingId, { status: 'done', logText: text })
        .then(() => { setLogging(false); setSelectedKey(null); load() })
        .catch(() => { /* server refuses without a log — box stays open */ })
    } else {
      updateBooking(selected.bookingId, { status: 'done' }).then(() => { setSelectedKey(null); load() })
    }
  }

  return createPortal(
    <>
      {/* Draggable right-edge dock tab */}
      <button
        onPointerDown={tabDown}
        title="Calls — your day's calls & meetings (drag to move)"
        className="fixed right-0 z-[900] px-1.5 py-3 rounded-l-lg text-white text-[11px] font-bold tracking-widest shadow-md select-none touch-none"
        style={{ top: tabY, background: '#16A34A', writingMode: 'vertical-rl', cursor: 'grab' }}
      >
        <IconPhone className="w-4 h-4 inline -mt-0.5 mr-1" />CALLS
      </button>

      {open && (
        <div className="fixed inset-y-0 right-0 z-[950] w-[420px] max-w-[94vw] bg-surface border-l border-border shadow-float flex flex-col">
          {/* Header: day nav + mini calendar */}
          <div className="px-4 py-3 border-b border-border shrink-0">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[15px] font-bold text-txt-primary flex-1">Calls & Meetings</p>
              <button onClick={() => { setOpen(false); window.location.assign('/crm/my-day?tab=today') }}
                className="px-2 py-1 rounded-lg text-[10px] font-bold text-brand-700 border border-brand-200 bg-brand-50 hover:bg-brand-100" title="Open My Day on the Today tab">
                Go to My Day →
              </button>
              <button onClick={() => setOpen(false)} className="p-1 rounded-lg text-txt-tertiary hover:text-txt-primary hover:bg-surface-hover">✕</button>
            </div>
            <div className="flex items-center gap-1.5 mt-2 relative">
              <button onClick={() => shiftDay(-1)} className="w-7 h-7 rounded-lg border border-border text-txt-secondary hover:bg-surface-hover" title="Previous day">‹</button>
              <button
                onClick={() => setCalOpen((v) => !v)}
                className="flex-1 px-2 py-1.5 rounded-lg border border-border text-[13px] font-semibold text-txt-primary hover:bg-surface-hover"
                title="Pick a date"
              >
                {dayLabel} ▾
              </button>
              <button onClick={() => shiftDay(1)} className="w-7 h-7 rounded-lg border border-border text-txt-secondary hover:bg-surface-hover" title="Next day">›</button>
              {day !== todayIso && (
                <button onClick={() => setDay(todayIso)} className="px-2 py-1.5 rounded-lg text-[11px] font-bold text-brand-600 border border-brand-300 hover:bg-brand-50">Today</button>
              )}
              {calOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setCalOpen(false)} />
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-20">
                    <MiniCalendar day={day} onPick={(iso) => { setDay(iso); setCalOpen(false) }} />
                  </div>
                </>
              )}
            </div>
            {/* All-day strip */}
            {allDay.length > 0 && (
              <div className="mt-2 space-y-1">
                {allDay.map((it) => (
                  <div key={it.key} className="rounded-md border border-dashed border-border bg-surface-secondary px-2 py-1">
                    <p className="text-[10px] font-semibold text-txt-secondary truncate"><IconCalendarUi className="w-3 h-3 inline -mt-0.5" /> {it.title} · all day{it.sub ? ` · ${it.sub}` : ''}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* The day grid */}
          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
            {feed === null ? (
              <p className="text-[12px] text-txt-tertiary px-4 py-4">Loading your day…</p>
            ) : (
              <div className="relative mx-2 my-2" style={{ height: gridH }}>
                {/* Hour lines */}
                {Array.from({ length: endH - startH + 1 }, (_, i) => (
                  <div key={i} className="absolute left-0 right-0 flex items-start pointer-events-none" style={{ top: i * HOUR_H }}>
                    <span className="w-11 pr-1.5 text-right text-[9px] text-txt-tertiary -translate-y-1/2">{pad(startH + i)}:00</span>
                    <div className="flex-1 border-t border-border/70" />
                  </div>
                ))}
                {Array.from({ length: endH - startH }, (_, i) => (
                  <div key={`h${i}`} className="absolute left-12 right-0 border-t border-border/30 pointer-events-none" style={{ top: i * HOUR_H + HOUR_H / 2 }} />
                ))}

                {/* Event blocks — clickable */}
                {timed.map((it) => {
                  const s = it.informational ? INFO_STYLE : KIND_STYLE[it.kind]
                  const from = minsOf(it.startAt!)
                  const to = it.endAt ? minsOf(it.endAt) : from + 30
                  const sel = selectedKey === it.key
                  const lane = lanes[it.key] || 0
                  const n = cols[it.key] || 1
                  return (
                    <button
                      key={it.key}
                      onClick={() => { setSelectedKey(sel ? null : it.key); setLogging(false) }}
                      className="absolute rounded-lg px-2 py-1 text-left overflow-hidden transition-shadow"
                      style={{
                        top: topOf(from) + 1,
                        height: heightOf(from, to),
                        // Side-by-side columns within the cluster: gutter 48px, 6px right margin.
                        left: `calc(48px + (100% - 54px) * ${lane} / ${n} + ${lane ? 3 : 0}px)`,
                        width: `calc((100% - 54px) / ${n} - ${n > 1 ? 3 : 0}px)`,
                        background: s.bg,
                        border: `${it.overdue ? '2px solid #DC2626' : `1.5px ${s.dashed ? 'dashed' : 'solid'} ${s.border}`}`,
                        opacity: it.status === 'done' || it.status === 'cancelled' ? 0.55 : 1,
                        boxShadow: sel ? '0 0 0 2px #2563EB' : '0 1px 2px rgba(0,0,0,0.06)',
                        zIndex: sel ? 20 : 10 + lane,
                      }}
                    >
                      <p className="text-[11px] font-bold truncate leading-snug" style={{ color: s.text, textDecoration: it.status === 'cancelled' ? 'line-through' : undefined }}>
                        {s.icon} {it.title}
                        {it.status === 'done' ? ' ✓' : ''}
                      </p>
                      {it.status === 'cancelled' && (
                        <span className="inline-block px-1 rounded text-[8px] font-bold uppercase text-white leading-tight" style={{ background: '#DC2626' }}>Cancelled</span>
                      )}
                      {heightOf(from, to) > 40 && (
                        <p className="text-[10px] truncate leading-snug" style={{ color: s.text, opacity: 0.75 }}>
                          {fmtTime(it.startAt)}{it.endAt ? `–${fmtTime(it.endAt)}` : ''}{it.sub ? ` · ${it.sub}` : ''}
                        </p>
                      )}
                    </button>
                  )
                })}

                {/* Now line */}
                {isToday && nowMin >= gridMin && nowMin <= endH * 60 && (
                  <div className="absolute left-0 right-0 pointer-events-none z-30" style={{ top: topOf(nowMin) }}>
                    <div className="flex items-center">
                      <span className="w-11 pr-1 text-right text-[9px] font-bold text-red-600 -translate-y-1/2">{pad(Math.floor(nowMin / 60))}:{pad(nowMin % 60)}</span>
                      <div className="flex-1 border-t-2 border-red-500" />
                    </div>
                  </div>
                )}

                {timed.length === 0 && (
                  <div className="absolute inset-x-0 top-24 text-center pointer-events-none">
                    <p className="text-sm text-txt-secondary">Nothing scheduled.</p>
                    <p className="text-[11px] text-txt-tertiary mt-1">Book calls and meetings from any lead — they appear here.</p>
                  </div>
                )}
              </div>
            )}
            {feed && !feed.google.available && (
              <p className="text-[10px] text-txt-tertiary text-center pb-2">Google Calendar unavailable — showing CRM bookings and demos only.</p>
            )}
          </div>

        </div>
      )}

      {/* The detail card — a CENTRED modal (Lee: the footer strip looked bad and was
          hard to read). Backdrop click or ✕ closes; actions are proper buttons. */}
      {selected && (
        <div
          className="fixed inset-0 z-[970] flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) { setSelectedKey(null); setLogging(false) } }}
        >
          <div className="w-full max-w-md rounded-2xl bg-surface border border-border shadow-float overflow-hidden">
            {/* Header */}
            <div className="px-5 pt-4 pb-3 border-b border-border" style={{ background: (selected.informational ? INFO_STYLE : KIND_STYLE[selected.kind]).bg }}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: (selected.informational ? INFO_STYLE : KIND_STYLE[selected.kind]).text, opacity: 0.7 }}>
                    {selected.informational ? 'Colleague’s demo — info only' : selected.kind === 'google' ? 'Google Calendar' : selected.kind === 'demo' ? 'Calendly demo' : selected.kind === 'call' ? 'Scheduled call' : 'Scheduled meeting'}
                  </p>
                  <p className="text-[16px] font-bold text-txt-primary mt-0.5 leading-snug">{KIND_STYLE[selected.kind].icon} {selected.title}</p>
                  {selected.informational && (
                    <p className="text-[12px] mt-0.5" style={{ color: '#374151' }}>
                      Shown so you can book around it — you don’t need to join this one.
                    </p>
                  )}
                  <p className="text-[13px] text-txt-secondary mt-0.5">
                    {new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })} · {fmtTime(selected.startAt)}{selected.endAt ? `–${fmtTime(selected.endAt)}` : ''}
                  </p>
                </div>
                <button onClick={() => { setSelectedKey(null); setLogging(false) }} className="p-1.5 rounded-lg text-txt-tertiary hover:text-txt-primary hover:bg-black/5 shrink-0">✕</button>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap mt-2">
                {selected.overdue && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase text-white" style={{ background: '#DC2626' }}>Overdue</span>}
                {selected.status === 'done' && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase" style={{ background: '#DCFCE7', color: '#166534' }}>Done</span>}
                {selected.status === 'cancelled' && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase" style={{ background: '#FEE2E2', color: '#991B1B' }}>Cancelled</span>}
                {selected.dealName && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-brand-50 text-brand-700 border border-brand-200">
                    {selected.dealName}{selected.matchedBy === 'domain' ? ' · via company domain' : ''}
                  </span>
                )}
              </div>
            </div>

            {/* Facts */}
            <div className="px-5 py-4 space-y-3 max-h-[45vh] overflow-y-auto">
              {selected.contactName && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary">Contact</p>
                  <p className="text-[13px] text-txt-primary font-medium mt-0.5">{selected.contactName}</p>
                </div>
              )}
              {selected.phone && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary">Phone</p>
                  <p className="text-[13px] text-txt-primary font-medium mt-0.5">{selected.phone}</p>
                </div>
              )}
              {selected.attendees && selected.attendees.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary">Attendees</p>
                  <div className="flex items-center gap-1.5 flex-wrap mt-1">
                    {selected.attendees.map((a) => (
                      <span key={a} className="px-2 py-1 rounded-full text-[11px] font-medium bg-surface-secondary border border-border text-txt-secondary">{a}</span>
                    ))}
                  </div>
                </div>
              )}
              {selected.note && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary">Note</p>
                  <p className="text-[13px] text-txt-primary whitespace-pre-wrap mt-0.5">{selected.note}</p>
                </div>
              )}
              {selected.logText && (
                <div className="rounded-xl bg-surface-secondary border border-border px-3 py-2.5">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary">Call log</p>
                  <p className="text-[13px] text-txt-primary whitespace-pre-wrap mt-0.5">{selected.logText}</p>
                  {(selected.logLinks || []).map((l, i) => (
                    <a key={i} href={l} target="_blank" rel="noreferrer" className="block text-[12px] text-brand-600 hover:underline truncate mt-0.5"><IconLink className="w-3 h-3 inline -mt-0.5" /> {l}</a>
                  ))}
                </div>
              )}
              {logging && (
                <div className="space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary">Log the call — required to complete</p>
                  <textarea
                    value={logText}
                    onChange={(e) => setLogText(e.target.value)}
                    placeholder="What happened, outcome, next steps…"
                    rows={3}
                    autoFocus
                    className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-[13px] text-txt-primary placeholder:text-txt-tertiary focus:outline-none focus:border-brand-400 resize-none"
                  />
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="px-5 py-3.5 border-t border-border bg-surface-secondary flex items-center gap-2 flex-wrap">
              {logging ? (
                <>
                  <button onClick={() => setLogging(false)} className="px-3 py-2 rounded-lg text-[12px] font-medium text-txt-secondary hover:bg-surface-hover">Cancel</button>
                  <button onClick={completeSelected} disabled={!logText.trim()} className="px-4 py-2 rounded-lg text-[12px] font-bold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 ml-auto">Save log & complete</button>
                </>
              ) : (
                <>
                  {selected.kind === 'call' && selected.phone && selected.status === 'planned' && (
                    <a href={`tel:${selected.phone.replace(/[\s()-]/g, '')}`} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12px] font-bold text-white bg-emerald-600 hover:bg-emerald-700"><IconPhone className="w-3.5 h-3.5" /> Call</a>
                  )}
                  {selected.meetUrl && selected.status !== 'done' && selected.status !== 'cancelled' && (
                    <a href={selected.meetUrl} target="_blank" rel="noreferrer" className="px-3.5 py-2 rounded-lg text-[12px] font-bold text-white" style={{ background: '#2F6FB0' }}>Join Meet</a>
                  )}
                  {selected.bookingId && selected.status === 'planned' && bookingById[selected.bookingId] && (
                    <button
                      onClick={() => { setEditing({ booking: bookingById[selected.bookingId!], dealId: selected.dealId!, dealName: selected.dealName || undefined }); setSelectedKey(null) }}
                      className="px-3.5 py-2 rounded-lg text-[12px] font-semibold border border-border text-txt-primary hover:bg-surface-hover bg-surface"
                      title="Edit — Google is updated and attendees re-notified"
                    >
                      ✎ Edit
                    </button>
                  )}
                  {selected.bookingId && selected.status === 'planned' && (
                    <button onClick={completeSelected} className="px-3.5 py-2 rounded-lg text-[12px] font-semibold border border-border text-txt-primary hover:bg-surface-hover bg-surface">✓ Complete</button>
                  )}
                  {/* Cancelled: the follow-up TASK is automatic (lead + to-do) — the only
                      manual action left is booking the replacement. */}
                  {selected.bookingId && selected.status === 'cancelled' && bookingById[selected.bookingId] && selected.dealId && (
                    <button
                      onClick={() => { setFollowingUp({ booking: bookingById[selected.bookingId!], dealId: selected.dealId!, dealName: selected.dealName || undefined }); setSelectedKey(null) }}
                      className="px-3.5 py-2 rounded-lg text-[12px] font-bold text-white bg-brand-600 hover:bg-brand-700"
                    >
                      Book follow-up
                    </button>
                  )}
                  {selected.dealId && (
                    <button
                      onClick={() => {
                        setDealLoading(true)
                        getDealById(selected.dealId!).then((d) => setOpenDeal(d)).catch(() => { /* lead may be archived */ }).finally(() => setDealLoading(false))
                      }}
                      className="px-3.5 py-2 rounded-lg text-[12px] font-bold text-brand-600 border border-brand-300 hover:bg-brand-50 ml-auto"
                    >
                      {dealLoading ? 'Opening…' : 'Open lead →'}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Full lead popup, opened from the detail card. */}
      {openDeal && <LeadRedirect deal={openDeal} onDone={() => setOpenDeal(null)} />}

      {/* Edit a booking — Google patched + attendees re-notified. */}
      {editing && (
        <BookingModal
          dealId={editing.dealId}
          dealName={editing.dealName}
          editBooking={editing.booking}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load() }}
        />
      )}
      {/* Book a follow-up for a cancelled booking — prefilled from the original. */}
      {followingUp && (
        <BookingModal
          dealId={followingUp.dealId}
          dealName={followingUp.dealName}
          initialType={followingUp.booking.type}
          initial={{ title: `Follow-up: ${followingUp.booking.title}`, attendees: followingUp.booking.attendees, note: followingUp.booking.note || undefined }}
          onClose={() => setFollowingUp(null)}
          onSaved={() => { setFollowingUp(null); load() }}
        />
      )}
    </>,
    document.body,
  )
}
